import { Dataset, JSONObject } from '../../types/types';
import { getFilterParamObject } from '../apiRequests/getFilterParamObject';
import { stripEmptyActiveFilters } from '../apiRequests/stripEmptyActiveFilters';
import { resolveStringExpression } from '../json/resolveStringExpression';
import { DATASET_ITEM_VISIBILITY_KEYS } from '../../constants';
import { hasDynamicItemVisibility } from './hasDynamicItemVisibility';
import { debugLogger } from '../../debug/DebugLogger';

const log = debugLogger.scope('visibility');

// Evaluates item visibility JSONata expressions against { datasetId, filters }
export const resolveDatasetItemsVisibility = async (
  dataset: Dataset,
  activeFilters: Record<string, unknown> | null
): Promise<Dataset> => {
  if (!hasDynamicItemVisibility(dataset)) {
    return dataset;
  }

  // Same { name: { op: value } } shape the API receives in the filter param
  const filters = getFilterParamObject(
    stripEmptyActiveFilters(activeFilters ?? {})
  );
  const resolveData: JSONObject = {
    datasetId: dataset.datasetId ?? '',
    filters,
  };

  const itemPromises = (dataset.items ?? []).map(async (item) => {
    const expressionKeys = DATASET_ITEM_VISIBILITY_KEYS.filter(
      (key) => typeof item[key] === 'string'
    );
    if (!expressionKeys.length) {
      return item;
    }

    const resolvedItem = { ...item };
    for (const key of expressionKeys) {
      const expression = item[key] as string;
      try {
        const resolved = await resolveStringExpression(expression, resolveData);
        resolvedItem[key] = Boolean(resolved);
      } catch (error) {
        console.error(
          '[resolveDatasetItemsVisibility] Failed to evaluate visibility expression.',
          { item: item.name, key, expression, error }
        );

        /** @debugFeature visibility Logs a failed dataset item visibility expression — that key defaults to hidden. */
        log('visibility expression failed, defaulting to hidden', () => ({
          itemName: item.name,
          key,
          expression,
          error,
        }));

        // Mirror useVisibilityResolve: a failing expression hides the item
        resolvedItem[key] = false;
      }
    }
    return resolvedItem;
  });

  const items = await Promise.all(itemPromises);

  return { ...dataset, items };
};
